import { Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listProblems } from '@/services/problemsApi';
import type { Difficulty, ProblemFilters } from '@/services/types';
import { queryKeys } from '@/lib/queryKeys';
import PageLoading from '@/components/common/PageLoading';
import ContentApiErrorState from '@/components/common/ContentErrorState';
import EmptyState from '@/components/common/EmptyState';

const difficultyMap = {
  EASY: { label: '简单', color: 'text-green-600 bg-green-50' },
  MEDIUM: { label: '中等', color: 'text-yellow-600 bg-yellow-50' },
  HARD: { label: '困难', color: 'text-red-500 bg-red-50' },
};

export default function ProblemsPage() {
  const [search, setSearch] = useState('');
  const [difficulty, setDifficulty] = useState<Difficulty | 'ALL'>('ALL');
  const [category, setCategory] = useState('');

  const filters: ProblemFilters = {
    difficulty: difficulty === 'ALL' ? undefined : difficulty,
    category: category.trim() || undefined,
  };

  const {
    data: problems,
    isLoading,
    isError,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.problems.list(filters),
    queryFn: () => listProblems(filters),
    retry: 1,
  });

  const visibleProblems = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) {
      return problems ?? [];
    }

    return (problems ?? []).filter((problem) => {
      return problem.title.toLowerCase().includes(keyword) || problem.id.toLowerCase().includes(keyword);
    });
  }, [problems, search]);

  if (isLoading) {
    return <PageLoading message='题目列表加载中…' />;
  }

  return (
    <div className='max-w-[980px] mx-auto px-6 py-12'>
      <h1
        className='font-semibold text-apple-near-black mb-2'
        style={{ fontSize: '2rem', lineHeight: 1.1, letterSpacing: '-0.02em' }}
      >
        Python 题目库
      </h1>
      <p className='text-apple-gray mb-10' style={{ fontSize: '0.9375rem' }}>
        从基础语法到算法挑战，系统提升编程能力。
      </p>

      <div className='bg-white rounded-2xl p-4 shadow-card-sm mb-6 grid grid-cols-1 md:grid-cols-3 gap-3'>
        <input
          className='input'
          placeholder='搜索题目标题或 ID'
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <select
          className='input'
          value={difficulty}
          onChange={(event) => setDifficulty(event.target.value as Difficulty | 'ALL')}
        >
          <option value='ALL'>全部难度</option>
          <option value='EASY'>简单</option>
          <option value='MEDIUM'>中等</option>
          <option value='HARD'>困难</option>
        </select>
        <input
          className='input'
          placeholder='按分类筛选（如 array）'
          value={category}
          onChange={(event) => setCategory(event.target.value)}
        />
      </div>

      {isError ? (
        <div className='mb-8'>
          <ContentApiErrorState error={queryError} onRetry={() => void refetch()} />
        </div>
      ) : null}

      {!isError && visibleProblems.length === 0 ? (
        <EmptyState
          title='没有匹配的题目'
          description='可以尝试清空筛选条件或更换关键词。'
          action={
            <button
              type='button'
              className='btn btn-secondary'
              onClick={() => {
                setSearch('');
                setDifficulty('ALL');
                setCategory('');
              }}
            >
              重置筛选
            </button>
          }
        />
      ) : null}

      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
        {visibleProblems.map((problem) => {
          const diff = difficultyMap[problem.difficulty] ?? difficultyMap.EASY;
          return (
            <Link
              key={problem.id}
              to={`/problems/${problem.id}`}
              className={`bg-white rounded-2xl p-5 shadow-card-sm hover:shadow-card transition-shadow block ${
                problem.completed ? 'ring-1 ring-green-200' : ''
              }`}
            >
              <div className='flex items-start justify-between gap-2 mb-3'>
                <h3
                  className='font-medium text-apple-near-black'
                  style={{ fontSize: '0.9375rem', lineHeight: 1.4 }}
                >
                  {problem.title}
                </h3>
                <span
                  className={`shrink-0 text-[0.6875rem] font-medium px-2 py-0.5 rounded-full ${diff.color}`}
                >
                  {diff.label}
                </span>
              </div>
              {problem.completed && (
                <span className='text-[0.8125rem] text-green-600 font-medium'>✓ 已完成</span>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
